import { useEffect, useRef, useState } from 'react';
import styles from './imageModal.module.scss';
import { Svgs } from '../../assets';

type Props = {
  photos: { url: string; index: number }[];
  index: number;
  onClose: () => void;
};

const MIN_SCALE = 1;
const MAX_SCALE = 4;

export function ImageModal({ photos, index, onClose }: Props) {
  const [currentIndex, setCurrentIndex] = useState(index);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  const pointers = useRef<Map<number, PointerEvent>>(new Map());
  const lastDistance = useRef<number | null>(null);
  const isDragging = useRef(false);
  const lastPoint = useRef({ x: 0, y: 0 });

  const resetTransform = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const setPage = (page: number) => {
    resetTransform();
    setCurrentIndex(page);
  };

  const nextImage = () => {
    resetTransform();
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const prevImage = () => {
    resetTransform();
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  /* ESC key close */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* Wheel Zoom (PC) */
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    let newScale = scale - e.deltaY * 0.001;
    newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));
    setScale(newScale);
  };

  /* Pointer Down */
  const handlePointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, e.nativeEvent);

    if (pointers.current.size === 1 && scale > 1) {
      isDragging.current = true;
      lastPoint.current = { x: e.clientX, y: e.clientY };
    }
  };

  /* Pointer Move */
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, e.nativeEvent);

    // Pinch Zoom
    if (pointers.current.size === 2) {
      const [p1, p2] = Array.from(pointers.current.values());
      const dx = p1.clientX - p2.clientX;
      const dy = p1.clientY - p2.clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (lastDistance.current) {
        let newScale = scale * (distance / lastDistance.current);
        newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));
        setScale(newScale);
      }

      lastDistance.current = distance;
      return;
    }

    // Drag Move
    if (isDragging.current && scale > 1) {
      const dx = e.clientX - lastPoint.current.x;
      const dy = e.clientY - lastPoint.current.y;

      setPosition((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));

      lastPoint.current = { x: e.clientX, y: e.clientY };
    }
  };

  /* Pointer Up */
  const handlePointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    lastDistance.current = null;
    isDragging.current = false;
  };

  const handleDoubleClick = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      resetTransform();
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalTop}>
        <div className={styles.modalIndex}>
          {currentIndex + 1} / {photos.length}
        </div>
        <button className={styles.closeBtn} onClick={onClose} aria-label="닫기">
          <Svgs.Close />
        </button>
      </div>

      <div
        ref={containerRef}
        className={styles.modal}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      >
        <img
          src={photos[currentIndex].url}
          className={styles.img}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging.current ? 'none' : 'transform 0.2s ease'
          }}
          draggable={false}
        />
        <div className={styles.pagination}>
          {photos.map((_, index) => {
            const isActive = currentIndex === index;
            return (
              <span
                key={index}
                className={[styles.page, !!isActive && styles.active].join(' ')}
                onClick={() => setPage(index)}
              />
            );
          })}
        </div>
      </div>
      <div className={styles.arrowContainer}>
        <button
          className={[styles.iconBtn, styles.prev].join(' ')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            prevImage();
          }}
          aria-label="이전 사진"
        >
          <Svgs.Arrow />
        </button>
        <button
          className={[styles.iconBtn, styles.next].join(' ')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            nextImage();
          }}
          aria-label="다음 사진"
        >
          <Svgs.Arrow />
        </button>
      </div>
    </div>
  );
}
